import { useEffect, useState } from 'react';
import { Input, Checkbox } from '@/components/fields';
import { checkIfSubscriptionTheme } from '@/utils/helpers';
import { useTheme } from '@/contexts/ThemeContext';

export default function Confirmation({ slug }: { slug: string }): JSX.Element {
  const theme = useTheme();
  const { landingPage } = theme;
  const IS_BANK = process.env.NEXT_PUBLIC_IS_BANK === 'true';
  const TITLE = process.env.NEXT_PUBLIC_TITLE;
  const API_ENDPOINT = process.env.NEXT_PUBLIC_API_ENDPOINT;
  const [variant, setVariant] = useState('1');
  const [price, setPrice] = useState('');
  const [termsCKOlink, setTermsCKOlink] = useState('');
  const [privacyCKOlink, setPrivacyCKOlink] = useState('');
  const [customerId, setCustomerId] = useState<string>(null);
  const [isSubscriptionTheme, setIsSubscriptionTheme] = useState(false);
  const termsAndConditionsLink = (
    <a
      href="/terms-and-conditions"
      target="_blank"
      className="confirmation-link"
    >
      Terms and Conditions
    </a>
  );
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768); // Or any other breakpoint you choose
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  const privacyPolicyLink = (
    <a href="/privacy-policy" target="_blank" className="confirmation-link">
      Privacy Policy
    </a>
  );

  const privacyPolicyLinkMobile = (
    <span className="confirmation-link">Privacy Policy</span>
  );

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const requestedVariant = params.get('v') as variant;
      const storedVariant = window.localStorage.getItem('v') as variant;
      const costumerId = window.localStorage.getItem('customerId');
      setCustomerId(costumerId);
      setIsSubscriptionTheme(
        checkIfSubscriptionTheme(theme.slug, theme.landingPage)
      );

      if (['1', '2', '3', '4'].includes(requestedVariant)) {
        setVariant(requestedVariant);
        window.localStorage.setItem('v', requestedVariant);
      } else if (storedVariant !== null) {
        setVariant(storedVariant);
      }

      const fetchProduct = async () => {
        const res = await fetch(`${API_ENDPOINT}/products/${slug}`);
        const json = await res.json();

        if (json.success) {
          const { price } = json.data;
          setPrice(price);
        }
      };

      fetchProduct();

      if (IS_BANK) {
        const domain = window.location.origin.replace('apply.', '');
        setTermsCKOlink(`${domain}/terms-and-conditions`);
        setPrivacyCKOlink(`${domain}/privacy-policy`);
      }
    }
  }, []);

  const { slug: themeSlug } = theme;
  const isV4andCheckboxPrice =
    landingPage === 'v1' &&
    themeSlug === 'checkbox-price' &&
    isSubscriptionTheme;

  return (
    <>
      <div>
        {landingPage !== 'v12' && landingPage !== 'v13' && (
          <h3 className="bg-box-header text-xl leading-8 font-medium tracking-tight text-white flex items-center mb-5 py-2 px-4">
            Confirmation
          </h3>
        )}
        <div className="px-6 py-1">
          {!customerId && (
            <>
              <Input id="email" isRequired>
                Email Address
              </Input>
              <Input id="confirm_email" isRequired>
                Confirm Email Address
              </Input>
            </>
          )}
          <Input
            id="mobile_number"
            isRequired={false}
            placeholder="07911123456"
          >
            Phone Number
            <br />
            <span className="font-medium text-xs text-red-600">
              UK Phone number to receive SMS updates at each stage of the
              application process.
            </span>
          </Input>
          <div className="mt-4 md:flex md:items-center">
            <div className="md:w-2/5 md:pr-4">
              <span className="text-sm font-normal">
                Confirm Details <sup className="text-red-600 font-sans">*</sup>
              </span>
            </div>
            <div className="md:w-2/5 md:pl-4">
              <Checkbox
                id="consent"
                name="consent"
                checked={false}
                booleanValue
              >
                {isV4andCheckboxPrice ? (
                  <p>
                    I have read and agree to the {termsAndConditionsLink} and
                    the {privacyPolicyLink}, and would like to subscribe for £89
                    every 3 months after the 3 days free trial. I would also
                    like to proceed my application with {TITLE} and this service
                    immediately. I understand and accept that by requesting
                    this, I will lose my right to withdraw from this contract
                    once the service has been fully performed.
                  </p>
                ) : isSubscriptionTheme ? (
                  <>
                    {landingPage === 'v7' ? (
                      <p>
                        I have read and agree to the {termsAndConditionsLink}{' '}
                        and the{' '}
                        {isMobile ? privacyPolicyLinkMobile : privacyPolicyLink}
                        , and would like to subscribe after the free trial. I
                        will be able to cancel my subscription at any time. I
                        would also like to proceed my application and this
                        service immediately. I understand and accept that by
                        requesting this that I will lose my right to withdraw
                        from my application once the service has been fully
                        performed.
                      </p>
                    ) : IS_BANK ? (
                      <p>
                        I have read and agree to the{' '}
                        <a
                          className="confirmation-link"
                          href={termsCKOlink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Terms and Conditions
                        </a>{' '}
                        and the{' '}
                        <a
                          className="confirmation-link"
                          href={privacyCKOlink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          Privacy Policy
                        </a>
                        . I would also like to proceed my application with
                        Driver&apos;s Licence Assistance and this service
                        immediately. I understand and accept that by requesting
                        this that I will lose my right to withdraw from this
                        contract once the service has been fully performed.
                      </p>
                    ) : (
                      <p>
                        I have read and agree to the {termsAndConditionsLink}{' '}
                        and the{' '}
                        {isMobile ? privacyPolicyLinkMobile : privacyPolicyLink}
                        , and would like to subscribe after the free trial. I
                        will be able to cancel my subscription at any time. I
                        would also like to proceed my application and this
                        service immediately. I understand and accept that by
                        requesting this that I will lose my right to withdraw
                        from my application once the service has been fully
                        performed.
                      </p>
                    )}
                  </>
                ) : IS_BANK ? (
                  <p>
                    I have read and agree to the{' '}
                    <a
                      className="confirmation-link"
                      href={termsCKOlink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Terms and Conditions
                    </a>{' '}
                    and the
                    <a
                      className="confirmation-link"
                      href={privacyCKOlink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Privacy Policy
                    </a>
                    . I would also like to proceed my application with
                    Driver&apos;s Licence Assistance and this service
                    immediately. I understand and accept that by requesting this
                    that I will lose my right to withdraw from this contract
                    once the service has been fully performed.
                  </p>
                ) : (
                  <p>
                    I have read and agree to the {termsAndConditionsLink} and
                    the {privacyPolicyLink}, and would like to proceed my
                    application with {TITLE}
                    {['3', '4'].includes(variant) ? ` for £${price}` : ''}.
                  </p>
                )}
              </Checkbox>
            </div>
          </div>
          {!IS_BANK && (
            <div className="mt-4 md:flex md:items-center">
              <div className="md:w-2/5 md:pr-4">
                <span className="text-sm font-normal">Fast Track</span>
              </div>
              <div className="md:w-2/5 md:pl-4">
                <Checkbox
                  id="priority"
                  name="priority"
                  checked={true}
                  booleanValue
                >
                  <p>
                    Add fast track option to process your driving licence within
                    2 working days - This additional option, charged £10, allows
                    you to fast track your application. This means your
                    application will be prioritised, checked and processed
                    within 2 working days. If you untick you will not be charged
                    for this service.
                  </p>
                </Checkbox>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
